import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  SURVEY_STATUS_QUERY_KEY,
  useSnoozeSurvey,
  useSubmitSurvey,
  useSurveyStatus,
} from 'innovate/api/api';
import {
  SurveyAnswer,
  SurveyAnswerValue,
  SurveyQuestion,
  SurveyTemplate,
} from 'innovate/utils/surveyTypes';
import useToastNotification from 'innovate/utils/useToastNotification';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const SURVEY_HANDLED_KEY = 'survey_handled';

type FormState = Record<string, SurveyAnswerValue>;

const isAnswered = (question: SurveyQuestion, value: SurveyAnswerValue | undefined): boolean => {
  if (value === null || value === undefined) return false;
  switch (question.type) {
    case 'rating':
      return typeof value === 'number';
    case 'multi_choice':
      return Array.isArray(value) && value.length > 0;
    default:
      return typeof value === 'string' && value.trim().length > 0;
  }
};

const buildAnswers = (questions: SurveyQuestion[], form: FormState): SurveyAnswer[] =>
  questions.map((q) => ({
    question_id: q.id,
    question: q.label,
    type: q.type,
    answer: form[q.id] ?? (q.type === 'text' ? '' : null),
  }));

const getErrorMessage = (err?: { status?: number; data?: unknown }): string => {
  if (err?.status === 400) {
    const detail = (err.data as { detail?: unknown } | undefined)?.detail;
    return typeof detail === 'string' && detail
      ? detail
      : 'Some answers are invalid. Please review and try again.';
  }
  if (err?.status === 413) {
    return 'Your answers are too long. Please shorten your text answers and try again.';
  }
  return 'Something went wrong while submitting your feedback. Please try again.';
};

interface QuestionFieldProps {
  question: SurveyQuestion;
  value: SurveyAnswerValue | undefined;
  onChange: (value: SurveyAnswerValue) => void;
  disabled: boolean;
}

const ratingCategories = {
  detractors: { label: 'Detractors', bg: '#FFE5E8', emphasis: '#FFD0D5', border: '#E798A1' },
  passives: { label: 'Passives', bg: '#FFF5CC', emphasis: '#FFECB0', border: '#D9BC60' },
  promoters: { label: 'Promoters', bg: '#E8F5DC', emphasis: '#D8EDC5', border: '#A2C580' },
};

const getRatingCategory = (rating: number) => {
  if (rating <= 6) return ratingCategories.detractors;
  if (rating <= 8) return ratingCategories.passives;
  return ratingCategories.promoters;
};

const RatingField: React.FC<QuestionFieldProps> = ({ question, value, onChange, disabled }) => {
  const min = question.scale?.min ?? 0;
  const max = question.scale?.max ?? 10;
  const values = Array.from({ length: Math.max(max - min + 1, 0) }, (_, i) => min + i);
  const isLikelihoodScale = min === 0 && max === 10;
  return (
    <Box>
      <Grid
        templateColumns={`repeat(${values.length || 1}, minmax(0, 1fr))`}
        gap={{ base: '3px', sm: '6px' }}
        role="group"
        aria-label={question.label}
      >
        {values.map((n) => {
          const rating = isLikelihoodScale
            ? getRatingCategory(n)
            : {
                label: `${n} out of ${max}`,
                bg: '#F1F1F1',
                emphasis: '#E2E2E2',
                border: '#666666',
              };
          return (
            <Tooltip key={n} label={rating.label} hasArrow placement="top">
              <Button
                variant="unstyled"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="100%"
                h="36px"
                minW={0}
                borderRadius="10px"
                border="1px solid"
                borderColor={value === n ? rating.border : 'gray.200'}
                bg={value === n ? rating.emphasis : rating.bg}
                color="#141414"
                boxShadow={value === n ? `inset 0 0 0 1px ${rating.border}` : 'none'}
                px={0}
                fontSize="12px"
                fontWeight={value === n ? '600' : '400'}
                transition="background-color 150ms ease, border-color 150ms ease"
                _hover={{ bg: rating.emphasis, borderColor: rating.border }}
                _active={{ bg: rating.emphasis }}
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: rating.border,
                  outlineOffset: '2px',
                }}
                aria-pressed={value === n}
                isDisabled={disabled}
                onClick={() => onChange(n)}
              >
                {n}
              </Button>
            </Tooltip>
          );
        })}
      </Grid>
      {isLikelihoodScale ? (
        <Flex justify="space-between" gap={2} mt={2} fontSize="11px" color="gray.500">
          <Text>Not at all likely</Text>
          <Text textAlign="right">Extremely likely</Text>
        </Flex>
      ) : null}
    </Box>
  );
};

const QuestionField: React.FC<QuestionFieldProps> = (props) => {
  const { question, value, onChange, disabled } = props;
  switch (question.type) {
    case 'rating':
      return <RatingField {...props} />;
    case 'text': {
      const text = typeof value === 'string' ? value : '';
      return (
        <Box>
          <Textarea
            aria-label={question.label}
            fontSize="12px"
            borderWidth="1px"
            borderColor="gray.300"
            focusBorderColor="#000000"
            _hover={{ borderColor: 'gray.400' }}
            _focus={{ borderColor: '#000000', boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ borderColor: '#000000', boxShadow: 'none', outline: 'none' }}
            placeholder={question.placeholder}
            maxLength={question.max_length}
            value={text}
            isDisabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          {question.max_length ? (
            <Text fontSize="10px" color="textSecondary" textAlign="right">
              {text.length}/{question.max_length}
            </Text>
          ) : null}
        </Box>
      );
    }
    case 'single_choice':
      return (
        <RadioGroup
          value={typeof value === 'string' ? value : ''}
          onChange={(v) => onChange(v)}
          isDisabled={disabled}
        >
          <Stack spacing={1}>
            {(question.options ?? []).map((opt) => (
              <Radio key={opt} value={opt}>
                <Text fontSize="12px">{opt}</Text>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      );
    case 'multi_choice':
      return (
        <CheckboxGroup
          value={Array.isArray(value) ? value : []}
          onChange={(v) => onChange(v as string[])}
          isDisabled={disabled}
        >
          <Stack spacing={1}>
            {(question.options ?? []).map((opt) => (
              <Checkbox
                key={opt}
                value={opt}
                sx={{
                  '.chakra-checkbox__control': {
                    borderWidth: '1px',
                    borderColor: 'gray.300',
                    _hover: { borderColor: 'gray.400' },
                    _checked: {
                      bg: '#000000',
                      borderColor: '#000000',
                      color: '#FFFFFF',
                      _hover: { bg: '#000000', borderColor: '#000000' },
                    },
                    _focus: { boxShadow: 'none', outline: 'none' },
                    _focusVisible: {
                      boxShadow: 'none',
                      outline: 'none',
                      borderColor: '#000000',
                    },
                  },
                }}
              >
                <Text fontSize="12px">{opt}</Text>
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      );
    default:
      return null;
  }
};

const SurveyPopup: React.FC = () => {
  const queryClient = useQueryClient();
  const showToast = useToastNotification();
  const [handled, setHandled] = useState(
    () => sessionStorage.getItem(SURVEY_HANDLED_KEY) === 'true'
  );
  const [errorMessage, setErrorMessage] = useState('');

  const { data } = useSurveyStatus(!handled);
  const submitSurvey = useSubmitSurvey();
  const snoozeSurvey = useSnoozeSurvey();

  const status = data?.success ? data.data : null;
  const template = status?.should_show ? status.template : null;
  // const template = status?.should_show ? status.template : null;

  const isBusy = submitSurvey.isPending || snoozeSurvey.isPending;

  if (handled || !template) return null;

  const finish = () => {
    sessionStorage.setItem(SURVEY_HANDLED_KEY, 'true');
    queryClient.setQueryData(SURVEY_STATUS_QUERY_KEY, {
      success: true,
      simulation: false,
      data: { ...status, should_show: false, template: null },
    });
    setHandled(true);
  };

  const handleSnooze = async () => {
    if (isBusy) return;
    try {
      await snoozeSurvey.mutateAsync();
    } catch {
      // A failed snooze must never block the user
    }
    finish();
  };

  const handleSubmit = async (answers: SurveyAnswer[]) => {
    if (isBusy) return;
    setErrorMessage('');
    try {
      const result = await submitSurvey.mutateAsync({
        template_version: template.version,
        answers,
      });
      if (result.success) {
        showToast('', 'Thank you for your feedback!', 'success');
        finish();
        return;
      }
      const err = result.err;
      if (err?.status === 409) {
        // Survey not open or already submitted: close silently
        finish();
        return;
      }
      setErrorMessage(getErrorMessage(err));
    } catch {
      setErrorMessage(getErrorMessage());
    }
  };

  return (
    <SurveyPopupForm
      template={template}
      isBusy={isBusy}
      isSubmitting={submitSurvey.isPending}
      isSnoozing={snoozeSurvey.isPending}
      errorMessage={errorMessage}
      onSnooze={handleSnooze}
      onSubmit={handleSubmit}
    />
  );
};

interface SurveyPopupFormProps {
  template: SurveyTemplate;
  isBusy?: boolean;
  isSubmitting?: boolean;
  isSnoozing?: boolean;
  errorMessage?: string;
  onSnooze: () => void;
  onSubmit: (answers: SurveyAnswer[]) => void;
}

const SurveyPopupForm: React.FC<SurveyPopupFormProps> = ({
  template,
  isBusy = false,
  isSubmitting = false,
  isSnoozing = false,
  errorMessage = '',
  onSnooze: handleSnooze,
  onSubmit,
}) => {
  const initialFocusRef = useRef<HTMLElement>(null);
  const maybeLaterRef = useRef<HTMLButtonElement>(null);
  const dismissHintTimer = useRef<number | null>(null);
  const [showDismissHint, setShowDismissHint] = useState(false);
  const [form, setForm] = useState<FormState>({});

  useEffect(() => {
    if (isBusy) setShowDismissHint(false);
    return () => {
      if (dismissHintTimer.current !== null) {
        window.clearTimeout(dismissHintTimer.current);
        dismissHintTimer.current = null;
      }
    };
  }, [isBusy]);

  const handleOutsideClick = () => {
    if (isBusy) return;
    if (dismissHintTimer.current !== null) window.clearTimeout(dismissHintTimer.current);
    setShowDismissHint(true);
    maybeLaterRef.current?.focus({ preventScroll: true });
    dismissHintTimer.current = window.setTimeout(() => {
      setShowDismissHint(false);
      dismissHintTimer.current = null;
    }, 2000);
  };
  const questions = useMemo(
    () =>
      template.questions
        .map((q, index) => ({ q, index }))
        .sort((a, b) => (a.q.order ?? a.index) - (b.q.order ?? b.index))
        .map(({ q }) => q),
    [template]
  );
  const canSubmit = questions.every((q) => !q.required || isAnswered(q, form[q.id]));

  const handleSubmit = () => {
    if (isBusy || !canSubmit) return;
    onSubmit(buildAnswers(questions, form));
  };

  return (
    <Modal
      isOpen
      isCentered
      size="xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
      onOverlayClick={handleOutsideClick}
      closeOnEsc={false}
      initialFocusRef={initialFocusRef}
      onClose={handleSnooze}
    >
      <ModalOverlay />
      <ModalContent ref={initialFocusRef} data-testid="survey-popup">
        <ModalHeader
          mx={6}
          px={0}
          py={3}
          mb={2}
          borderBottom="1px solid"
          borderColor="blackAlpha.100"
          fontSize="16px"
          fontWeight="700"
        >
          {template.title}
        </ModalHeader>
        <ModalBody>
          {template.description ? (
            <Text fontSize="12px" mb={4}>
              {template.description}
            </Text>
          ) : null}
          <Stack spacing={5}>
            {questions.map((q, index) => (
              <Box key={q.id}>
                <Text fontSize="12px" fontWeight="700" mb={2}>
                  {index + 1}. {q.label}
                  {q.required ? (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  ) : null}
                </Text>
                <QuestionField
                  question={q}
                  value={form[q.id]}
                  disabled={isBusy}
                  onChange={(value) => setForm((prev) => ({ ...prev, [q.id]: value }))}
                />
              </Box>
            ))}
          </Stack>
          {errorMessage ? (
            <Text role="alert" fontSize="12px" color="red.500" mt={4}>
              {errorMessage}
            </Text>
          ) : null}
        </ModalBody>
        <ModalFooter mx={6} px={0} gap={3} borderTop="1px solid" borderColor="blackAlpha.100">
          <Button
            ref={maybeLaterRef}
            variant="secondary"
            size="sm"
            fontSize="12px"
            bg={showDismissHint ? '#F3F4F6' : undefined}
            borderColor={showDismissHint ? '#6B7280' : 'gray.300'}
            boxShadow="none"
            _focusVisible={{ boxShadow: 'none', outline: 'none', borderColor: '#6B7280' }}
            transition="background-color 150ms ease, border-color 150ms ease"
            _hover={{
              bg: showDismissHint ? '#F3F4F6' : 'gray.50',
              borderColor: showDismissHint ? '#6B7280' : 'gray.400',
            }}
            isDisabled={isBusy}
            isLoading={isSnoozing}
            loadingText="Maybe Later"
            aria-busy={isSnoozing}
            onClick={handleSnooze}
          >
            <Text color="#000000"> Maybe Later</Text>
          </Button>
          <Button
            variant="green"
            size="sm"
            fontSize="12px"
            border="none"
            color="white"
            _disabled={{ opacity: isSubmitting ? 1 : 0.4 }}
            _hover={{
              _disabled: { background: '#508A3B', bg: '#508A3B', color: 'white' },
            }}
            isDisabled={!canSubmit || isBusy}
            isLoading={isSubmitting}
            loadingText="Submit Feedback"
            aria-busy={isSubmitting}
            onClick={handleSubmit}
          >
            <Text color="#FFFFFF">Submit Feedback</Text>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SurveyPopup;
